import { If, TabLink } from "components";
import { GET_LIST_REQUEST_DEFAULT_LIMIT } from "constants/common";
import { useBoolean } from "hooks";
import { BoxShadowLayout, ContentLayout } from "layouts";
import { Routes } from "modules/common";
import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  Input,
  LoaderScreen,
  Modal,
  Pagination,
  SearchInput,
  TableFilter
} from "ui";

import {
  useResellersTabsCounters,
  useUpdateResellerBlockStatus
} from "../../apis";
import {
  ChangeResellerDeleteStatusModal,
  ResellersFilterForm,
  ResellersTable
} from "../../components";
import { useResellersList } from "../../hooks";

import styles from "./Resellers.module.scss";

const Resellers = () => {
  const { t } = useTranslation();

  const usersRoutes = useMemo(() => {
    return Routes.find((route) => {
      return route.label === "Users";
    })?.nestedRoutes;
  }, [Routes]);

  const { state } = useLocation();

  const {
    resellers,
    isLoading,
    sorting,
    setSorting,
    refetch,
    page,
    totalItems,
    setPage,
    searchValue,
    setSearchValue
  } = useResellersList({
    defaultSorting: state?.defaultSorting
  });

  const { countersByLevel, allCountersTotal } = useResellersTabsCounters();

  const [actionConfirmingResellerId, setActionConfirmingResellerId] = useState<
    string | null
  >(null);

  const [blockReason, setBlockReason] = useState("");

  const {
    value: isBlockModalOpen,
    setTrue: openBlockModal,
    setFalse: closeBlockModal
  } = useBoolean(false);

  const {
    value: isChangeDeleteStatusModalOpen,
    setTrue: openChangeDeleteStatusModal,
    setFalse: closeChangeDeleteStatusModal
  } = useBoolean(false);

  const updateResellerBlockStatus = useUpdateResellerBlockStatus({
    onSuccess: (_, variables) => {
      closeBlockModal();
      setActionConfirmingResellerId(null);
      toast.success(
        variables.action === "block"
          ? "User has been  blocked successfully"
          : "User has been  unblocked successfully"
      );
      setBlockReason("");
      refetch();
    }
  });

  const isActionConfirmingResellerDeleted = useMemo(() => {
    return resellers?.find(
      (reseller) => reseller.id === actionConfirmingResellerId
    )?.isDeleted;
  }, [resellers, actionConfirmingResellerId]);

  return (
    <ContentLayout title={t("Users")} counter={totalItems}>
      <BoxShadowLayout className={styles.pageContent}>
        <If condition={!!usersRoutes}>
          <nav>
            {usersRoutes?.map((route) => {
              const counter =
                route.label === "All"
                  ? allCountersTotal
                  : (countersByLevel?.find(
                      (levelCounter) =>
                        typeof route.path === "string" &&
                        route.path.includes(levelCounter.level)
                    )?.count ?? 0);

              return (
                <TabLink
                  key={route.label}
                  pathName={route.path}
                  label={route.label}
                  counterValue={counter}
                />
              );
            })}
          </nav>
        </If>

        <div className={styles.pageContent_tableHeader_controls}>
          <SearchInput
            value={searchValue}
            onChange={setSearchValue}
            placeholder={`${t("Search")}...`}
          />

          <TableFilter>
            {(props) => <ResellersFilterForm {...props} />}
          </TableFilter>
        </div>

        <div className={styles.pageContent_tableContainer}>
          <ResellersTable
            items={resellers ?? []}
            isLoading={isLoading}
            sorting={sorting}
            setSorting={setSorting}
            onChangeDeleteStatus={(reseller) => {
              setActionConfirmingResellerId(reseller.id);
              openChangeDeleteStatusModal();
            }}
            onChangeBlockStatus={(reseller) => {
              if (reseller.isBlocked) {
                updateResellerBlockStatus.mutate({
                  resellerId: reseller.id,
                  action: "unblock"
                });
              } else {
                setActionConfirmingResellerId(reseller.id);
                openBlockModal();
              }
            }}
          />
        </div>

        <Pagination
          currentPage={page}
          onChange={setPage}
          perPage={GET_LIST_REQUEST_DEFAULT_LIMIT}
          totalItems={totalItems}
        />
      </BoxShadowLayout>

      <ChangeResellerDeleteStatusModal
        isOpen={isChangeDeleteStatusModalOpen}
        resellerId={String(actionConfirmingResellerId)}
        action={isActionConfirmingResellerDeleted ? "Restore" : "Delete"}
        onClose={closeChangeDeleteStatusModal}
      />

      <Modal
        isOpen={isBlockModalOpen}
        title="Block Reseller"
        isLoading={updateResellerBlockStatus.isLoading}
        okText="Block"
        isOkDisabled={blockReason.length < 1}
        onOk={() =>
          updateResellerBlockStatus.mutate({
            resellerId: String(actionConfirmingResellerId),
            reason: blockReason,
            action: "block"
          })
        }
        onClose={closeBlockModal}
      >
        <Input
          asTag={"textarea"}
          placeholder="Enter a reason"
          value={blockReason}
          onChange={(e) => setBlockReason(e.target.value)}
        />
      </Modal>

      {updateResellerBlockStatus.isLoading && <LoaderScreen />}
    </ContentLayout>
  );
};

export default Resellers;
